import {
	forwardRef,
	useEffect,
	useImperativeHandle,
	useRef,
	useState,
} from 'react';
import Error from '../Error/Error';

// Site key is public; the secret stays in the backend.
const SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

// Reserve space so the form doesn't jump.
const WIDGET_HEIGHT = 65;

/** Cloudflare Turnstile widget. Call reset() via ref after a failed login. */
const Turnstile = forwardRef(({ onToken, error, touched }, ref) => {
	const containerRef = useRef(null);
	const widgetIdRef = useRef(null);
	const [loadFailed, setLoadFailed] = useState(false);

	useImperativeHandle(ref, () => ({
		reset: () => {
			if (widgetIdRef.current !== null && window.turnstile) {
				window.turnstile.reset(widgetIdRef.current);
			}
			onToken('');
		},
	}));

	useEffect(() => {
		let cancelled = false;

		const render = () => {
			if (cancelled || !containerRef.current || widgetIdRef.current !== null) return;

			widgetIdRef.current = window.turnstile.render(containerRef.current, {
				sitekey: SITE_KEY,
				callback: (token) => onToken(token),
				'expired-callback': () => onToken(''),
				'error-callback': () => onToken(''),
			});
		};

		// Script loads async; wait until it's ready.
		if (window.turnstile) {
			render();
		} else {
			const startedAt = Date.now();
			const poll = setInterval(() => {
				if (window.turnstile) {
					clearInterval(poll);
					render();
				} else if (Date.now() - startedAt > 10000) {
					clearInterval(poll);
					if (!cancelled) setLoadFailed(true);
				}
			}, 100);
			return () => {
				cancelled = true;
				clearInterval(poll);
			};
		}

		return () => {
			cancelled = true;
		};
	}, [onToken]);

	if (loadFailed) {
		return (
			<div style={{ minHeight: WIDGET_HEIGHT }}>
				<Error>
					Could not load the security check. Please refresh the page or check your
					connection.
				</Error>
			</div>
		);
	}

	return (
		<div style={{ minHeight: WIDGET_HEIGHT }}>
			<div ref={containerRef} />
			{touched && error ? <Error>{error}</Error> : null}
		</div>
	);
});

Turnstile.displayName = 'Turnstile';

export default Turnstile;
